import { useCallback, useMemo, useState } from 'react';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import {
  detectCardNetwork,
  formatCardExpiry,
  getExpiryParts,
  isLuhnValid,
  validateCardPaymentFormValues,
} from '../utils/cardPaymentFormValidation';

export type CardFormatterStateControls = {
  setCardNumber: (value: string) => void;
  setCardNumberError: (value: string | null) => void;
  setIsCardNumberValid: (value: boolean) => void;
  setCardNetwork: (value: string) => void;
  setCvvLength: (value: number) => void;
  setCardCvv: Dispatch<SetStateAction<string>>;
  setCardExpiry: (value: string) => void;
  setIsRazorpayExpiryInvalid: (value: boolean) => void;
};

export function useCardPaymentState() {
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [cardNetwork, setCardNetwork] = useState('');
  const [, setIsCardNumberValid] = useState(false);
  const [cardNumberError, setCardNumberError] = useState<string | null>(null);
  const [cvvLength, setCvvLength] = useState(3);
  const [isRazorpayExpiryInvalid, setIsRazorpayExpiryInvalid] = useState(false);

  const checkIsPastExpiry = useCallback((expiry: string): boolean => {
    const [monthValue, yearValue] = getExpiryParts(expiry);
    if (monthValue.length !== 2 || yearValue.length !== 2) return false;

    const month = parseInt(monthValue, 10);
    const year = parseInt(yearValue, 10) + 2000;

    if (isNaN(month) || isNaN(year)) return false;
    if (month < 1 || month > 12) return true;

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;

    return year < currentYear || (year === currentYear && month < currentMonth);
  }, []);

  const handleCardNumberChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 19);
    const chunks = raw.match(/.{1,4}/g) || [];
    const formatted = chunks.join(' ');
    const detectedNetwork = detectCardNetwork(raw);
    const expectedLength = detectedNetwork === 'amex' ? 15 : raw.startsWith('50') || detectedNetwork === 'maestro' ? 19 : 16;
    const hasExpectedLength = raw.length === expectedLength;
    const validByFallback = hasExpectedLength && isLuhnValid(raw);

    setCardNumber(formatted);
    setCardNetwork(detectedNetwork);
    setCvvLength(detectedNetwork === 'amex' ? 4 : 3);
    setCardCvv((current) => current.slice(0, detectedNetwork === 'amex' ? 4 : 3));
    setIsCardNumberValid(validByFallback);
    setCardNumberError(hasExpectedLength && !validByFallback ? 'Enter a valid card number' : null);
  }, []);

  const handleExpiryChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardExpiry(e.target.value);
    setCardExpiry(formatted);
    const isComplete = formatted.replace(/\D/g, '').length === 4;
    setIsRazorpayExpiryInvalid(isComplete && checkIsPastExpiry(formatted));
  }, [checkIsPastExpiry]);

  const handleCvvChange = useCallback((value: string) => {
    setCardCvv(value.replace(/\D/g, '').slice(0, cvvLength));
  }, [cvvLength]);

  const validateCardForm = useCallback(() => {
    const validation = validateCardPaymentFormValues({
      cardName,
      cardNumber,
      cardExpiry,
      cardCvv,
      cvvLength,
    });

    return validation.isValid && !cardNumberError && !isRazorpayExpiryInvalid;
  }, [
    cardCvv,
    cardExpiry,
    cardName,
    cardNumber,
    cardNumberError,
    cvvLength,
    isRazorpayExpiryInvalid,
  ]);

  const formatterControls = useMemo<CardFormatterStateControls>(() => ({
    setCardNumber,
    setCardNumberError,
    setIsCardNumberValid,
    setCardNetwork,
    setCvvLength,
    setCardCvv,
    setCardExpiry,
    setIsRazorpayExpiryInvalid,
  }), []);

  return {
    cardName,
    setCardName,
    cardNumber,
    cardExpiry,
    cardCvv,
    setCardCvv: handleCvvChange,
    saveCard,
    setSaveCard,
    cardNetwork,
    cardNumberError,
    cvvLength,
    handleCardNumberChange,
    handleExpiryChange,
    validateCardForm,
    formatterControls,
    isCardFormValid: validateCardForm(),
    isExpiryInvalid: cardExpiry.replace(/\D/g, '').length === 4 && (
      checkIsPastExpiry(cardExpiry) || isRazorpayExpiryInvalid
    ),
  };
}
